import { useEffect, useRef } from 'react';
import styles from './HeroCanvas.module.css';

const PARTICLE_COLOR = '65, 176, 56';
const CONNECTION_DISTANCE = 140;

function HeroCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();

    const isMobile = window.innerWidth <= 768;
    const count = isMobile ? 38 : 80;

    const particles = Array.from({ length: count }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * (isMobile ? 0.25 : 0.38),
      vy: (Math.random() - 0.5) * (isMobile ? 0.25 : 0.38),
      radius: Math.random() > 0.72 ? 2.2 : 1.3,
      opacity: 0.4 + Math.random() * 0.55,
      pulse: Math.random() * Math.PI * 2, // phase offset for pulsing
    }));

    let animId;
    let frame = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;

      particles.forEach((p) => {
        // Move
        p.x += p.vx;
        p.y += p.vy;

        // Wrap edges
        if (p.x < -5) p.x = canvas.width + 5;
        if (p.x > canvas.width + 5) p.x = -5;
        if (p.y < -5) p.y = canvas.height + 5;
        if (p.y > canvas.height + 5) p.y = -5;

        // Pulse opacity
        const pulseOpacity = p.opacity + Math.sin(frame * 0.018 + p.pulse) * 0.15;
        const finalOpacity = Math.max(0.1, Math.min(1, pulseOpacity));

        // Draw glow
        const glowRadius = p.radius * 3.5;
        const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowRadius);
        grd.addColorStop(0, `rgba(${PARTICLE_COLOR}, ${finalOpacity * 0.45})`);
        grd.addColorStop(1, `rgba(${PARTICLE_COLOR}, 0)`);
        ctx.beginPath();
        ctx.arc(p.x, p.y, glowRadius, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        // Draw dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${PARTICLE_COLOR}, ${finalOpacity})`;
        ctx.fill();
      });

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DISTANCE) {
            const opacity = (1 - dist / CONNECTION_DISTANCE) * 0.2;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(${PARTICLE_COLOR}, ${opacity})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      }

      animId = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      resize();
      // Reposition out-of-bounds particles
      particles.forEach((p) => {
        if (p.x > canvas.width) p.x = Math.random() * canvas.width;
        if (p.y > canvas.height) p.y = Math.random() * canvas.height;
      });
    };
    window.addEventListener('resize', handleResize);

    if (!prefersReduced) {
      animate();
    }

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return <canvas ref={canvasRef} className={styles.heroCanvas} aria-hidden="true" />;
}

export default HeroCanvas;
